import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'complete' | 'error';
}

// Sanitize filename for SEO
const sanitizeFilename = (filename: string): string => {
  const parts = filename.split('.');
  const extension = parts.pop();
  const name = parts.join('.');
  
  const sanitized = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 100);
  
  return extension ? `${sanitized}.${extension}` : sanitized;
};

export const useMediaUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress[]>([]);

  const uploadFiles = async (files: File[], folderId?: string | null) => {
    setUploading(true);
    const results = [];

    for (const file of files) {
      try {
        // Validate file
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 10MB limit`);
          continue;
        }

        // Update progress
        setProgress(prev => [...prev, {
          fileName: file.name,
          progress: 0,
          status: 'uploading'
        }]);

        // Sanitize and generate unique filename
        const sanitizedName = sanitizeFilename(file.name);
        const fileExt = sanitizedName.split('.').pop();
        const fileName = `${Date.now()}-${sanitizedName}`;
        const filePath = `${fileName}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('media-library')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get image dimensions
        const dimensions = await getImageDimensions(file);

        // Get user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Update progress
        setProgress(prev => prev.map(p =>
          p.fileName === file.name ? { ...p, progress: 50, status: 'processing' } : p
        ));

        // Create database record
        const { data, error: dbError } = await supabase
          .from('media_library')
          .insert({
            user_id: user.id,
            filename: sanitizedName,
            storage_path: filePath,
            file_size: file.size,
            original_size: file.size,
            mime_type: file.type,
            width: dimensions.width,
            height: dimensions.height,
            folder_id: folderId,
          })
          .select(`
            id, filename, storage_path, file_size, mime_type,
            alt_text, title, width, height,
            thumbnail_path, medium_path, large_path,
            original_size, compressed_size, compression_ratio,
            is_public, folder_id, created_at, updated_at
          `)
          .single();

        if (dbError) throw dbError;

        // Update progress
        setProgress(prev => prev.map(p =>
          p.fileName === file.name ? { ...p, progress: 100, status: 'complete' } : p
        ));

        results.push(data);
        toast.success(`${file.name} uploaded successfully`);
      } catch (error: any) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}: ${error.message}`);
        setProgress(prev => prev.map(p =>
          p.fileName === file.name ? { ...p, status: 'error' } : p
        ));
      }
    }

    setUploading(false);
    setProgress([]);
    return results;
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  return {
    uploadFiles,
    uploading,
    progress,
  };
};
